import { useEffect, useState } from "react";
import { AlertTriangle, ExternalLink, Loader2, Beaker } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * OrphanCatalogWidget — muestra los pilotos "ready-for-market" sin vínculo a portfolio,
 * para forzar limpieza de datos en el Catálogo Innovaciones.
 */
export function OrphanCatalogWidget() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token) { setLoading(false); return; }
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/innovations/ready-for-market/orphans`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) setData(await r.json());
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="rounded-2xl border border-border bg-card p-4" data-testid="orphan-catalog-widget-loading">
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground"/>
      </div>
    );
  }

  if (!data) return null;

  const count = data.count || 0;

  // Estado limpio — muestra card verde compacta
  if (count === 0) {
    return (
      <div className="rounded-2xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50/40 dark:bg-emerald-500/5 p-4 flex items-center gap-3" data-testid="orphan-catalog-widget">
        <div className="w-10 h-10 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
          <Beaker className="w-5 h-5"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-emerald-800 dark:text-emerald-200">Catálogo Innovaciones al día</div>
          <div className="text-xs text-muted-foreground">Las {data.total_market} soluciones ready-for-market están vinculadas a Portfolio o a una iniciativa PG-07.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border-2 border-amber-400 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-500/10 p-4 space-y-3" data-testid="orphan-catalog-widget">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center flex-shrink-0 animate-pulse">
          <AlertTriangle className="w-5 h-5"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-black text-amber-900 dark:text-amber-200">
            {count === 1 ? "1 innovación huérfana" : `${count} innovaciones huérfanas`}
          </div>
          <div className="text-xs text-amber-800/80 dark:text-amber-300/80">
            Sin vínculo a Portfolio — {Math.round((data.orphan_ratio || 0) * 100)}% del catálogo requiere limpieza.
          </div>
        </div>
      </div>
      <ul className="space-y-1 text-xs">
        {(data.items || []).slice(0, 4).map((it, i) => (
          <li key={it.id} className="flex items-center justify-between gap-2 pl-3 pr-2 py-1 rounded-lg bg-white/60 dark:bg-slate-900/60" data-testid={`orphan-item-${i}`}>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{it.name}</div>
              <div className="text-[10px] text-muted-foreground">{it.decision} · {it.orphan_reason.replace(/_/g, " ")}</div>
            </div>
            {it.partner && <span className="text-[10px] text-muted-foreground truncate max-w-[80px]">{it.partner}</span>}
          </li>
        ))}
        {count > 4 && (
          <li className="text-[10px] text-amber-700 dark:text-amber-300 text-center pt-1">
            … y {count - 4} más
          </li>
        )}
      </ul>
      <button
        onClick={() => navigate("/app-catalog")}
        className="w-full text-xs font-bold py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white flex items-center justify-center gap-1 transition-colors"
        data-testid="orphan-catalog-widget-cta"
      >
        Abrir catálogo para limpiar <ExternalLink className="w-3 h-3"/>
      </button>
    </div>
  );
}
